import React, { useState, useMemo, useEffect } from 'react';
import {
  Plus,
  ChevronDown,
  ChevronUp,
  Search,
  Bug,
  Star,
  Lightbulb,
  BookOpen,
  AlertCircle,
  CheckCircle,
  Clock,
  Eye
} from 'lucide-react';
import apiService from './services/api';
import { Button, LoadingSpinner } from './components';
import TrackingDetailModal from './TrackingDetailModal';
import TrackingFormModal from './TrackingFormModal';

function TrackingPage() {
  const [activeTab, setActiveTab] = useState('bugs');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all'); // For bugs
  const [priorityFilter, setPriorityFilter] = useState('all'); // For ideas
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const itemsPerPage = 15;

  // API state
  const [trackingData, setTrackingData] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_severity: {}, by_priority: {}, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Fetch data based on active tab
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      let data = [];
      let statsData = { total: 0, by_status: {}, by_severity: {}, by_priority: {}, by_category: {} };

      switch (activeTab) {
        case 'bugs':
          data = await apiService.getBugs({ limit: 200 });
          statsData = await apiService.getBugStats();
          break;
        case 'features':
          data = await apiService.getFeatures({ limit: 200 });
          statsData = await apiService.getFeatureStats();
          break;
        case 'ideas':
          data = await apiService.getIdeas({ limit: 200 });
          statsData = await apiService.getIdeaStats();
          break;
        case 'research':
          data = await apiService.getResearch({ limit: 200 });
          statsData = await apiService.getResearchStats();
          break;
        default:
          break;
      }

      setTrackingData(Array.isArray(data) ? data : []);
      setStats(statsData);
    } catch (err) {
      setError(err.message || 'Failed to load tracking data');
      console.error('Error loading tracking data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when active tab changes
  useEffect(() => {
    fetchData();
    setCurrentPage(1);
    setSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setSeverityFilter('all');
    setPriorityFilter('all');
  }, [activeTab]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...trackingData];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const searchLower = searchTerm.toLowerCase();
        return (
          (item.title && item.title.toLowerCase().includes(searchLower)) ||
          (item.name && item.name.toLowerCase().includes(searchLower)) ||
          (item.description && item.description.toLowerCase().includes(searchLower)) ||
          (item.summary && item.summary.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }

    // Apply severity filter (bugs only)
    if (activeTab === 'bugs' && severityFilter !== 'all') {
      filtered = filtered.filter(item => item.severity === severityFilter);
    }

    // Apply priority filter (ideas only)
    if (activeTab === 'ideas' && priorityFilter !== 'all') {
      filtered = filtered.filter(item => item.priority === priorityFilter);
    }

    // Sort data
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'created_at' || sortField === 'updated_at') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [trackingData, searchTerm, statusFilter, categoryFilter, severityFilter, priorityFilter, sortField, sortDirection, activeTab]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Handle view details
  const handleViewDetails = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  // Handle create
  const handleCreate = () => {
    setEditingItem(null);
    setShowCreateModal(true);
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setShowCreateModal(true);
    setShowDetailModal(false);
  };

  // Handle delete
  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete this ${activeTab.slice(0, -1)}?`)) {
      return;
    }

    try {
      switch (activeTab) {
        case 'bugs':
          await apiService.deleteBug(item.id);
          break;
        case 'features':
          await apiService.deleteFeature(item.id);
          break;
        case 'ideas':
          await apiService.deleteIdea(item.id);
          break;
        case 'research':
          await apiService.deleteResearch(item.id);
          break;
        default:
          break;
      }
      fetchData();
      setShowDetailModal(false);
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  // Handle save (create or update)
  const handleSave = () => {
    fetchData();
    setShowCreateModal(false);
    setEditingItem(null);
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    // Only urgent/critical statuses get red-800, rest are gray
    const urgentStatuses = ['urgent', 'critical', 'regression'];
    if (urgentStatuses.includes(status)) {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-700';
  };

  // Get severity/priority badge color
  const getSeverityBadgeColor = (severity) => {
    // Only critical/urgent get red-800, rest are gray
    if (severity === 'critical' || severity === 'urgent') {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-700';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get unique categories for filter
  const uniqueCategories = useMemo(() => {
    const categories = [...new Set(trackingData.map(item => item.category).filter(Boolean))];
    return categories.sort();
  }, [trackingData]);

  // Calculate stats for display
  const displayStats = useMemo(() => {
    const total = filteredAndSortedData.length;
    const statusCounts = stats.by_status || {};

    return {
      total,
      open: statusCounts.open || statusCounts.proposed || statusCounts.new || 0,
      inProgress: statusCounts.in_progress || statusCounts.investigating || statusCounts.approved || statusCounts.pending || 0,
      closed: statusCounts.fixed || statusCounts.completed || statusCounts.implemented || statusCounts.validated || statusCounts.closed || 0
    };
  }, [filteredAndSortedData, stats]);

  if (loading && trackingData.length === 0) {
    return (
      <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto">
        <div className="h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 p-3 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="mb-3">
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Tracking System</h1>
        <p className="text-xs text-gray-600">Manage bugs, features, ideas, and research</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Items</p>
              <p className="text-lg font-semibold text-gray-800">{displayStats.total}</p>
            </div>
            <Bug className="w-3.5 h-3.5 text-red-800" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Open</p>
              <p className="text-lg font-semibold text-blue-600">{displayStats.open}</p>
            </div>
            <AlertCircle className="w-3.5 h-3.5 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">In Progress</p>
              <p className="text-lg font-semibold text-yellow-600">{displayStats.inProgress}</p>
            </div>
            <Clock className="w-3.5 h-3.5 text-yellow-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-2 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Completed</p>
              <p className="text-lg font-semibold text-green-600">{displayStats.closed}</p>
            </div>
            <CheckCircle className="w-3.5 h-3.5 text-green-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-3">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('bugs')}
            className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === 'bugs'
                ? 'border-b-2 border-red-800 text-red-800'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Bug className="w-3.5 h-3.5 mr-2" />
            Bugs
          </button>
          <button
            onClick={() => setActiveTab('features')}
            className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === 'features'
                ? 'border-b-2 border-red-800 text-red-800'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Star className="w-3.5 h-3.5 mr-2" />
            Features
          </button>
          <button
            onClick={() => setActiveTab('ideas')}
            className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === 'ideas'
                ? 'border-b-2 border-red-800 text-red-800'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Lightbulb className="w-3.5 h-3.5 mr-2" />
            Ideas
          </button>
          <button
            onClick={() => setActiveTab('research')}
            className={`flex items-center px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === 'research'
                ? 'border-b-2 border-red-800 text-red-800'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5 mr-2" />
            Research
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-2 shadow-sm mb-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
          >
            <option value="all">All Status</option>
            {activeTab === 'bugs' && (
              <>
                <option value="open">Open</option>
                <option value="investigating">Investigating</option>
                <option value="fixed">Fixed</option>
                <option value="wont_fix">Won't Fix</option>
                <option value="regression">Regression</option>
              </>
            )}
            {activeTab === 'features' && (
              <>
                <option value="active">Active</option>
                <option value="deprecated">Deprecated</option>
                <option value="planned">Planned</option>
              </>
            )}
            {activeTab === 'ideas' && (
              <>
                <option value="proposed">Proposed</option>
                <option value="approved">Approved</option>
                <option value="in_progress">In Progress</option>
                <option value="implemented">Implemented</option>
                <option value="rejected">Rejected</option>
                <option value="deferred">Deferred</option>
              </>
            )}
            {activeTab === 'research' && (
              <>
                <option value="validated">Validated</option>
                <option value="invalidated">Invalidated</option>
                <option value="pending">Pending</option>
                <option value="superseded">Superseded</option>
                <option value="archived">Archived</option>
              </>
            )}
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
          >
            <option value="all">All Categories</option>
            {uniqueCategories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          {/* Severity Filter (Bugs only) */}
          {activeTab === 'bugs' && (
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="all">All Severity</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          )}

          {/* Priority Filter (Ideas only) */}
          {activeTab === 'ideas' && (
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full px-2 py-1 text-xs bg-neutral-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-800"
            >
              <option value="all">All Priority</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="someday">Someday</option>
            </select>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-3">
          <p>{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center">
                    ID
                    {sortField === 'id' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />
                    )}
                  </div>
                </th>
                <th
                  className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort(activeTab === 'features' ? 'name' : 'title')}
                >
                  <div className="flex items-center">
                    {activeTab === 'features' ? 'Name' : 'Title'}
                    {sortField === (activeTab === 'features' ? 'name' : 'title') && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                  Status
                </th>
                {activeTab === 'bugs' && (
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                    Severity
                  </th>
                )}
                {activeTab === 'ideas' && (
                  <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                    Priority
                  </th>
                )}
                <th className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700">
                  Category
                </th>
                <th
                  className="px-2 py-1.5 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center">
                    Created
                    {sortField === 'created_at' && (
                      sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />
                    )}
                  </div>
                </th>
                <th className="px-2 py-1.5 text-center text-xs font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-2 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      {activeTab === 'bugs' && <Bug className="w-12 h-12 mb-3 text-gray-300" />}
                      {activeTab === 'features' && <Star className="w-12 h-12 mb-3 text-gray-300" />}
                      {activeTab === 'ideas' && <Lightbulb className="w-12 h-12 mb-3 text-gray-300" />}
                      {activeTab === 'research' && <BookOpen className="w-12 h-12 mb-3 text-gray-300" />}
                      <p className="text-xs">No {activeTab} found</p>
                      <button
                        onClick={handleCreate}
                        className="mt-3 px-4 py-2 text-xs bg-red-800 text-white rounded-lg hover:bg-red-900 transition-colors"
                      >
                        Create your first {activeTab.slice(0, -1)}
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedData.map(item => (
                  <tr key={item.id} className="hover:bg-neutral-50 cursor-pointer" onClick={() => handleViewDetails(item)}>
                    <td className="px-2 py-1.5 text-xs text-gray-800">#{item.id}</td>
                    <td className="px-2 py-1.5 text-xs text-gray-800">
                      {item.name || item.title}
                    </td>
                    <td className="px-2 py-1.5 text-xs">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(item.status)}`}>
                        {item.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    {activeTab === 'bugs' && (
                      <td className="px-2 py-1.5 text-xs">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityBadgeColor(item.severity)}`}>
                          {item.severity}
                        </span>
                      </td>
                    )}
                    {activeTab === 'ideas' && (
                      <td className="px-2 py-1.5 text-xs">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityBadgeColor(item.priority)}`}>
                          {item.priority}
                        </span>
                      </td>
                    )}
                    <td className="px-2 py-1.5 text-xs text-gray-600">{item.category || 'N/A'}</td>
                    <td className="px-2 py-1.5 text-xs text-gray-600">{formatDate(item.created_at)}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(item);
                        }}
                        className="text-red-800 hover:text-red-900"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-2 py-2 border-t border-gray-200 flex items-center justify-between">
            <div className="text-xs text-gray-600">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} items
            </div>
            <div className="flex gap-1">
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                variant={currentPage === 1 ? 'secondary' : 'primary'}
                size="sm"
              >
                Previous
              </Button>
              {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                const pageNum = currentPage <= 3 ? index + 1 : currentPage - 2 + index;
                if (pageNum > totalPages) return null;
                return (
                  <Button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    variant={currentPage === pageNum ? 'primary' : 'secondary'}
                    size="sm"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                variant={currentPage === totalPages ? 'secondary' : 'primary'}
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Floating Add Button */}
      <button
        onClick={handleCreate}
        className="fixed bottom-3 right-3 w-10 h-10 bg-red-800 rounded-lg flex items-center justify-center shadow-lg hover:bg-red-900 transition-colors"
      >
        <Plus className="w-4 h-4 text-white" />
      </button>

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <TrackingDetailModal
          item={selectedItem}
          type={activeTab}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedItem(null);
          }}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <TrackingFormModal
          type={activeTab}
          item={editingItem}
          onClose={() => {
            setShowCreateModal(false);
            setEditingItem(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

export default TrackingPage;
